import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { PeopleListingEntry } from "frontend/contentful/schema/blocks";
import {
  PeopleListing,
  PeopleListingProps,
} from "design-system/components/blocks/people-listing/people-listing";
import { createComponentHeaderProps } from "../primitives/component-header.props";

import { isContentType } from "frontend/contentful/lib/is-content-type";
import { useContentful } from "frontend/hooks/use-contentful";
import { usePageProps } from "frontend/hooks/use-page-props";
import {
  DetailPageEntry,
  ProgramPageEntry,
} from "frontend/contentful/schema/pages";
import { Alignment } from "design-system/types/types";
import { SocialMediaIconProps } from "design-system/components/primitives/social-media-links/social-media-icon";
import { PersonTeaseDisplayOptions } from "design-system/components/primitives/person-tease/person-tease";
import { getPersonProps } from "../utils/get-person.props";

interface PeopleListingOptions {
  headerAbove?: boolean;
}

export const ContentfulPeopleListing = createContentfulComponent<
  PeopleListingEntry,
  PeopleListingOptions
>(({ entry, options }) => {
  const { getEntry, getEntries } = useContentful();
  const { entry: pageEntry } = usePageProps();
  const page = getEntry(pageEntry);

  const isDetailPage = isContentType<DetailPageEntry>("detailPage", page);
  const isProgramPage = isContentType<ProgramPageEntry>("programPage", page);

  let alignment = "full";
  if (isDetailPage || isProgramPage) alignment = "wide";

  let emailVariant: PersonTeaseDisplayOptions["emailVariant"] = "icon";
  if (entry.fields.email === "Show as email address") emailVariant = "email";
  if (entry.fields.email === "Do not show email") emailVariant = "none";

  const props: PeopleListingProps = {
    people: [],
    emailVariant,
    hideBio: !entry.fields.bio,
    hidePhone: !entry.fields.phone,
    hideSocial: !entry.fields.social,
    align: alignment as Exclude<Alignment, "center">,
    header: createComponentHeaderProps(entry.fields.header),
  };

  getEntries(entry.fields.people)?.forEach((person) => {
    const personProps = getPersonProps(person);

    if (personProps) {
      // Add email icon to social media links
      if (props.emailVariant === "icon" && personProps.email) {
        const emailIcon: SocialMediaIconProps = {
          href: `mailto:${personProps.email}`,
          title: personProps.email,
          icon: "mail",
        };

        if (personProps.social?.links && !props.hideSocial) {
          personProps.social?.links.unshift(emailIcon);
        } else {
          personProps.social = {
            links: [emailIcon],
          };
        }
      }

      props.people.push(personProps);
    }
  });

  props.HeadingLevel = options?.headerAbove ? "h3" : "h2";
  props.HeadingLevelTease =
    options?.headerAbove && entry.fields.header
      ? "h4"
      : options?.headerAbove || entry.fields.header
        ? "h3"
        : "h2";

  return <PeopleListing {...props} />;
});
